import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDrag } from 'react-dnd';
import { Img } from 'react-image'
import AssetsLibraryUtilities from './AssetsLibraryUtilities';
import { AssetTypes, DndItemTypes } from '../../globals';
import { HelpIcon, Spinner, IconFillStyles } from '../Iconset';
import AssetsManager from "../../classes/AssetsManager";
import AssetUtility from "../../classes/AssetUtility";
import Spacer from '../layout/Spacer';
import { useTypedSelector } from '../../state/hooks';
import MaterialAsset from '../../classes/MaterialAsset';
import VisageTemplatePreview from '../visageUI/VisageTemplatePreview';

export default function AssetPreviewItem(props)
{
	const assetInfo = useMemo(() => AssetsManager.getAssetInfo(props.assetId), [props.assetId]);
	const assetProps = useMemo(() => AssetsManager.getPropsForAsset(props.assetId), [props.assetId]);
	const rootRef = useRef<HTMLDivElement>(null);
	const folderPath = useMemo((): {folder?: string, date?:string} | undefined => 
	{
		if(!assetProps) return undefined;
		const folders = new Array<string>();
		let currentFolder = assetProps.folder;
		let initialFolder = ""
		switch(assetProps.tag)
		{
			case "User":
				initialFolder = "Personal Assets";
				break;
			case "Organization":
				initialFolder = "Company Assets";
				break;
		}
		while(currentFolder)
		{
			const folderProps = AssetsManager.getPropsForAsset(currentFolder);
			if(folderProps)
			{
				folders.push(folderProps.label);
				currentFolder = folderProps.folder;
			}
			else break;
		}
		const date = assetProps?.creationDate ? new Date(assetProps.creationDate).toLocaleString("en-US", {
			month: "short",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZoneName: "short"}) : undefined;
		return {folder: initialFolder + "\\" + folders.reverse().join("\\"), date: date};
	}, [assetProps]);

	const [, drag] = useDrag({
		type: DndItemTypes.ASSET_PREVIEW_ITEM,
		item: () => 
		{
			return {
				'props': props,
				'selected': [{ index: 0, props: assetProps }],
			};
		},
	})

	if(!assetInfo || !assetProps) return null;

	const previewImageSrc = assetProps.type !== AssetTypes.Material ? AssetUtility.getPreviewAssetURL(assetInfo.id) : undefined;
	const scale = (props.scale || 1) * 50; // arbitrary scale factor
	const draggable = props.draggable !== undefined && props.draggable !== false;

	const assetIcon = useMemo(() => 
	{
		const icon = AssetsLibraryUtilities.getIconForAssetType(assetProps.type, 1, IconFillStyles.dark);
		return React.cloneElement(icon, { className: "iconRenderContainer assetPreviewIcon" });
	}, []);

	useEffect(() => 
	{
		if (assetInfo.type === AssetTypes.Material)
		{
			MaterialAsset.getMaterialAssetByGuid(props.assetId)?.renderPreview().then((blob) => 
			{
				if(blob) 
				{
					const url = URL.createObjectURL(blob);
					setMaterialPreviewSrc(url);
					// Clean up the URL when component unmounts or when blob changes
					return () => URL.revokeObjectURL(url);
				}
			});
		}
	}, []);

	const thumbnail = () => 
	{
		if (assetInfo.type === AssetTypes.VisageTemplate && rootRef.current)
		{
			return <VisageTemplatePreview templateId={assetProps.assetId} wrapperRect={rootRef.current.getBoundingClientRect()} />
		}
		
		return assetInfo.type === AssetTypes.Material ? 
			// TOOD: use proper react component
			// so this is a weird one
			// if I use the react Img tag it will only render *exactly* 256 material previews
			// I have no idea why, that number seems so specific that it doesn't seem to be related to timeouts or anything
			// so I'm using a plain html img tag instead for material previews. 
			materialPreviewSrc ? <img src={materialPreviewSrc} /> : <Spinner
				data-name={"asset-preview-item-thumbnail-spinner"}
				size={scale}
			/>
		 : previewImageSrc ? (
				<Img 
					src={previewImageSrc!}
					draggable={false}
					loader={
						<Spinner
							data-name={"asset-preview-item-thumbnail-spinner"}
							size={scale}
						/>
					}
					width={scale}
					height={scale}
					unloader={assetIcon ?? <HelpIcon />}
					onDragStart={(e) => e.preventDefault()}
				/>
			)  : (
				assetIcon ?? <HelpIcon />
			)
	}

	const [materialPreviewSrc, setMaterialPreviewSrc] = useState<string | undefined>(undefined);

	return (
		<div className={`assetPreviewContainer ${props.listItem ? "listItem" : ""} ${props.selectable ? " selectableAssetPreview" : ""}`}
			data-name={"asset-preview-container-" + assetProps.label}
			data-asset-id={props.assetId}
			ref={draggable ? drag : null}
			style={{
				width: props.listItem ? "100%" : (scale + 16),
			}}
			data-tooltip={props["tooltip"]}
			onClick={props.onClick}
			onDoubleClick={props.onDoubleClick}
			data-status={props.selected}
		>
			<div data-name={"asset-preview-item-thumbnail-image"}
				className={`assetPreviewImage ${!props.listItem ? "gridItem" : ""} ${assetInfo.type === AssetTypes.Material ? "materialItem" : ""} ${assetInfo.type === AssetTypes.VisageTemplate ? "visageTemplateItem" : ""}`}
				style={{
					width: props.listItem ? scale : "",
				}}
				ref={rootRef}
			>
				{thumbnail()}
			</div>
			{props.listItem && <Spacer size={{x: "8px"}} />}
			<div className="assetPreviewLabel"
				style={{
					width: props.listItem ? "" : (scale + 8),
					fontSize: props.listItem ? "12px" : "",
				}}
			>
				{assetProps.label}
			</div>
			{
				props.listItem && folderPath && !props.hideFolderNames && 
			<div style={{flex:1, color: "#A6A6A6", maxWidth: "360px"}}>
				<div>{folderPath.folder}</div>
				{folderPath.date && <div style={{fontSize: "10px", color: "#A6A6A6"}}>Imported {folderPath.date}</div>}
			</div>
			}
		</div>
	);
}
